/**
 * Procedurally generated matcap textures.
 *
 * Each preset generates a 256×256 canvas with a spherical shading pattern,
 * converted to a Three.js CanvasTexture. No external assets required.
 */

import * as THREE from "three/webgpu";

export interface MatcapPreset {
  label: string;
  /** Generate the matcap texture (cached after first call). */
  create: () => THREE.Texture;
}

const cache = new Map<string, THREE.Texture>();

function getOrCreate(key: string, fn: () => THREE.Texture): THREE.Texture {
  let tex = cache.get(key);
  if (!tex) {
    tex = fn();
    cache.set(key, tex);
  }
  return tex;
}

/** Render a matcap sphere into a canvas. */
function renderMatcap(
  size: number,
  colorFn: (nx: number, ny: number, nz: number) => [number, number, number],
): HTMLCanvasElement {
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d")!;
  const imageData = ctx.createImageData(size, size);
  const data = imageData.data;

  const half = size / 2;
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const nx = (x - half) / half;
      const ny = -(y - half) / half;
      const lenSq = nx * nx + ny * ny;
      const idx = (y * size + x) * 4;

      if (lenSq > 1) {
        data[idx] = 0;
        data[idx + 1] = 0;
        data[idx + 2] = 0;
        data[idx + 3] = 0;
        continue;
      }

      const nz = Math.sqrt(1 - lenSq);
      const [r, g, b] = colorFn(nx, ny, nz);
      data[idx] = Math.min(255, Math.max(0, Math.round(r * 255)));
      data[idx + 1] = Math.min(255, Math.max(0, Math.round(g * 255)));
      data[idx + 2] = Math.min(255, Math.max(0, Math.round(b * 255)));
      data[idx + 3] = 255;
    }
  }

  ctx.putImageData(imageData, 0, 0);
  return canvas;
}

function makeTexture(canvas: HTMLCanvasElement): THREE.Texture {
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

// --- Preset definitions ---

function clamp01(v: number) {
  return Math.min(1, Math.max(0, v));
}

function mix(a: number, b: number, t: number) {
  return a + (b - a) * t;
}

const SIZE = 256;

export const MATCAP_PRESETS: Record<string, MatcapPreset> = {
  clay: {
    label: "Clay",
    create: () =>
      getOrCreate("clay", () => {
        const canvas = renderMatcap(SIZE, (_nx, ny, nz) => {
          const light = clamp01(0.3 + 0.7 * nz + 0.2 * ny);
          const rim = 1 - nz;
          const v = mix(light, light * 0.7, rim * rim);
          return [v * 0.85, v * 0.82, v * 0.78];
        });
        return makeTexture(canvas);
      }),
  },

  softGray: {
    label: "Soft Gray",
    create: () =>
      getOrCreate("softGray", () => {
        const canvas = renderMatcap(SIZE, (_nx, ny, nz) => {
          const d = clamp01(0.2 + 0.8 * nz + 0.15 * ny);
          const v = d * 0.75 + 0.1;
          return [v, v, v * 1.02];
        });
        return makeTexture(canvas);
      }),
  },

  warmBrown: {
    label: "Warm Brown",
    create: () =>
      getOrCreate("warmBrown", () => {
        const canvas = renderMatcap(SIZE, (_nx, ny, nz) => {
          const d = clamp01(0.25 + 0.75 * nz + 0.15 * ny);
          return [d * 0.72, d * 0.55, d * 0.38];
        });
        return makeTexture(canvas);
      }),
  },

  pearl: {
    label: "Pearl",
    create: () =>
      getOrCreate("pearl", () => {
        const canvas = renderMatcap(SIZE, (nx, ny, nz) => {
          const d = clamp01(0.15 + 0.85 * nz + 0.1 * ny);
          const spec = clamp01(nz * 0.8 + ny * 0.2 + nx * 0.1) ** 32;
          const r = d * 0.92 + spec * 0.6;
          const g = d * 0.9 + spec * 0.65;
          const b = d * 0.95 + spec * 0.7;
          return [clamp01(r), clamp01(g), clamp01(b)];
        });
        return makeTexture(canvas);
      }),
  },

  redWax: {
    label: "Red Wax",
    create: () =>
      getOrCreate("redWax", () => {
        const canvas = renderMatcap(SIZE, (_nx, ny, nz) => {
          const d = clamp01(0.2 + 0.8 * nz + 0.15 * ny);
          const sss = clamp01(1 - nz) * 0.15;
          return [clamp01(d * 0.85 + sss), d * 0.22, d * 0.18];
        });
        return makeTexture(canvas);
      }),
  },

  jade: {
    label: "Jade",
    create: () =>
      getOrCreate("jade", () => {
        const canvas = renderMatcap(SIZE, (_nx, ny, nz) => {
          const d = clamp01(0.2 + 0.8 * nz + 0.12 * ny);
          const spec = clamp01(nz * 0.7 + ny * 0.3) ** 24;
          const sss = clamp01(1 - nz) * 0.1;
          return [
            clamp01(d * 0.2 + sss),
            clamp01(d * 0.6 + spec * 0.3 + sss),
            clamp01(d * 0.35 + spec * 0.15),
          ];
        });
        return makeTexture(canvas);
      }),
  },

  metal: {
    label: "Metal",
    create: () =>
      getOrCreate("metal", () => {
        const canvas = renderMatcap(SIZE, (nx, ny, nz) => {
          const d = clamp01(0.1 + 0.5 * nz + 0.2 * ny);
          const spec = clamp01(nz * 0.6 + ny * 0.4 + nx * 0.15) ** 16 * 0.8;
          const rim = (1 - nz) ** 3 * 0.15;
          const v = d + spec + rim;
          return [clamp01(v * 0.9), clamp01(v * 0.92), clamp01(v * 0.95)];
        });
        return makeTexture(canvas);
      }),
  },

  darkSteel: {
    label: "Dark Steel",
    create: () =>
      getOrCreate("darkSteel", () => {
        const canvas = renderMatcap(SIZE, (_nx, ny, nz) => {
          const d = clamp01(0.05 + 0.35 * nz + 0.1 * ny);
          const spec = clamp01(nz * 0.7 + ny * 0.3) ** 48 * 0.9;
          const rim = (1 - nz) ** 4 * 0.08;
          const v = d + spec + rim;
          return [clamp01(v * 0.7), clamp01(v * 0.72), clamp01(v * 0.78)];
        });
        return makeTexture(canvas);
      }),
  },

  skin: {
    label: "Skin",
    create: () =>
      getOrCreate("skin", () => {
        const canvas = renderMatcap(SIZE, (_nx, ny, nz) => {
          const d = clamp01(0.25 + 0.75 * nz + 0.12 * ny);
          const sss = clamp01(1 - nz) * 0.12;
          return [
            clamp01(d * 0.88 + sss * 1.2),
            clamp01(d * 0.65 + sss * 0.4),
            clamp01(d * 0.55 + sss * 0.2),
          ];
        });
        return makeTexture(canvas);
      }),
  },

  normals: {
    label: "Normals",
    create: () =>
      getOrCreate("normals", () => {
        const canvas = renderMatcap(SIZE, (nx, ny, nz) => {
          return [nx * 0.5 + 0.5, ny * 0.5 + 0.5, nz];
        });
        return makeTexture(canvas);
      }),
  },
};

export const MATCAP_PRESET_KEYS = Object.keys(MATCAP_PRESETS);
